import React, { ChangeEvent, useState } from 'react'

import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import FormSection from 'components_new/molecules/FormSection'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useOrganizationalUnitActions } from 'redux/actions/OrganizationalUnits'
import { useOrganizationalUnitsArray } from 'redux/hooks/OrganizationalUnits'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useAvailableUnits,
  useAvailableUnitsFetched
} from 'redux/hooks/AvailableUnits'

interface Props {
  onClose: () => void
  open: boolean
}

const AddDataAccessGroupDialog = React.forwardRef((props: Props, ref) => {
  const { onClose, open } = props
  const { tryCreateOrganizationalUnit } = useOrganizationalUnitActions()
  const { t } = useTranslation()
  const organizationalUnits = useOrganizationalUnitsArray()
  const { isCompanyBased } = useAccountSettings()
  const availableUnits = useAvailableUnits()
  const availableUnitsFetched = useAvailableUnitsFetched()

  const [name, setName] = useState('')
  const [selectedNames, setSelectedNames] = useState<string[]>([])

  const handleSave = async () => {
    try {
      await tryCreateOrganizationalUnit({
        name,
        names: selectedNames
      })
      setName('')
      setSelectedNames([])
      onClose()
    } catch (error) {
      console.error('Failed to create organizational unit:', error)
    }
  }

  const handleReset = () => {
    setName('')
    setSelectedNames([])
  }

  const handleClose = () => {
    onClose()
    handleReset()
  }

  const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }

  const handleChangeSelectedNames = (value: string[]) => {
    setSelectedNames(value)
  }

  // Check if name is empty or duplicate
  const isDuplicateName = organizationalUnits.some(
    (unit) => unit.name.trim() === name.trim()
  )
  const isNameEmpty = name.trim().length === 0
  const isSaveDisabled = isNameEmpty || isDuplicateName

  /*-- default --*/
  return (
    <SimpleDialog
      actions={
        <>
          <Button onClick={handleClose} variant="text">
            {t('global', 'cancel')}
          </Button>
          <Button disabled={isSaveDisabled} onClick={handleSave}>
            {t('global', 'save')}
          </Button>
        </>
      }
      hasClose={true}
      helperText={t(
        'data_access',
        isCompanyBased ? 'helper_text_company' : 'helper_text'
      )}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      ref={ref}
      title={`${t('global', 'add')} ${t('data_access', 'data_access', true)}`}
    >
      <FormSection
        disableMargin={true}
        importance="required"
        sx={{ mb: 1 }}
        title={t('global', 'title')}
      >
        <TextField
          autoFocus={true}
          error={isDuplicateName}
          helperText={
            isDuplicateName ? t('global', 'duplicate_name_error') : undefined
          }
          onChange={handleChangeName}
          placeholder={t('data_access', 'title_placeholder')}
          value={name}
        />
      </FormSection>
      <FormSection
        disableMargin={true}
        sx={{ mb: 1 }}
        title={t('org_units', isCompanyBased ? 'units_company' : 'units')}
      >
        <SearchAndSelect
          loading={!availableUnitsFetched}
          options={availableUnits.map((unit) => {
            return { id: unit, title: unit }
          })}
          onChange={handleChangeSelectedNames}
          value={selectedNames}
        />
      </FormSection>
    </SimpleDialog>
  )
})

export default AddDataAccessGroupDialog
